import {AiChatPublishType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {noop} from '@Utils/general.util';
import {getPosterHashedIdForAiChatImageAsset} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import type {PublishJob} from '@Components/ai-chat-window/lib/publish-flow.library';
import {runPublishFlow, runPublishInChat} from '@Components/ai-chat-window/lib/publish-flow.library';
import {AiChatAssetKind} from '@Components/ai-chat-window/lib/publish-flow.types';
import type {LoadingStates} from '@Utils/loading.util';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';

type SetPublishingLoadingState = (loadingOption: PublishingOption | null, loadingState: LoadingStates) => void;

const GA4_PUBLISH_TYPE: Record<PublishingOption, AiChatPublishType> = {
  [PublishingOption.PUBLISH_TO_SOCIAL_MEDIA]: AiChatPublishType.SOCIAL_MEDIA,
  [PublishingOption.PROMOTE_AS_EVENT]: AiChatPublishType.EVENT,
  [PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN]: AiChatPublishType.EMAIL,
};

const trackPublishAnalytics = (option: PublishingOption): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_PUBLISH, {[GA4EventParamName.TYPE]: GA4_PUBLISH_TYPE[option]});
};

const buildImagePublishJob = (option: PublishingOption, imageHashedId: string, setLoadingState: SetPublishingLoadingState): PublishJob => {
  return {
    option,
    trackAnalytics: (): void => {
      trackPublishAnalytics(option);
    },
    fetchPosterHashedId: (): Promise<string> => {
      return getPosterHashedIdForAiChatImageAsset(imageHashedId);
    },
    pageHashedId: null,
    setLoadingState,
  };
};

/**
 * Publishes an AI Chat image from a user gesture, so desktop may hand the wait to a new tab. Callers that own
 * menu rows pass a loading-state setter so those rows can spin.
 */
export const publishImageDesign = async (option: PublishingOption, imageHashedId: string, setLoadingState: SetPublishingLoadingState): Promise<void> => {
  await runPublishFlow({
    ...buildImagePublishJob(option, imageHashedId, setLoadingState),
    assetKind: AiChatAssetKind.IMAGE,
    hashedId: imageHashedId,
  });
};

/**
 * Publishes an AI Chat image on behalf of a stream control event. These fire outside any user gesture, so
 * they stay in the chat rather than attempting a tab the browser would block. There are no menu rows to
 * update from this path.
 */
const publishImageDesignFromControlEvent = async (option: PublishingOption, imageHashedId: string): Promise<void> => {
  await runPublishInChat(buildImagePublishJob(option, imageHashedId, noop));
};

const buildUserDesignPublishJob = (option: PublishingOption, posterHashedId: string, pageHashedId: string | null): PublishJob => {
  return {
    option,
    trackAnalytics: (): void => {
      trackPublishAnalytics(option);
    },
    fetchPosterHashedId: (): Promise<string> => Promise.resolve(posterHashedId),
    pageHashedId,
    setLoadingState: noop,
  };
};

export const postUserDesignOnSocialMedia = async (posterHashedId: string): Promise<void> => {
  await runPublishInChat(buildUserDesignPublishJob(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, posterHashedId, null));
};

export const promoteUserDesignAsEvent = async (posterHashedId: string, pageHashedId: string | null): Promise<void> => {
  await runPublishInChat(buildUserDesignPublishJob(PublishingOption.PROMOTE_AS_EVENT, posterHashedId, pageHashedId));
};

export const sendUserDesignAsEmail = async (posterHashedId: string, pageHashedId: string | null): Promise<void> => {
  await runPublishInChat(buildUserDesignPublishJob(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, posterHashedId, pageHashedId));
};

export const postDesignOnSocialMedia = async (imageHashedId: string): Promise<void> => {
  await publishImageDesignFromControlEvent(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, imageHashedId);
};

export const promoteDesignAsEvent = async (imageHashedId: string): Promise<void> => {
  await publishImageDesignFromControlEvent(PublishingOption.PROMOTE_AS_EVENT, imageHashedId);
};

export const sendDesignAsEmail = async (imageHashedId: string): Promise<void> => {
  await publishImageDesignFromControlEvent(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, imageHashedId);
};
